import type { PlaylistItem } from '@solinkcorp/motion-events-estimator';
import { scaleLinear, type ScaleLinear } from 'd3-scale';
import type { Immutable } from 'immer';
import { decodeMotionGrid } from './motionGridParser.ts';

const AI_VALUE_UPPER_BOUND = 255;
export const MAX_MOTION_INTENSITY = 3;

/**
 * If there is a gap in segment data that is longer than this, we'll add
 * a `#EXT-X-DISCONTINUITY` marker to the playlist if there isn't one there
 * already.
 */
export const MIN_GAP_FOR_DISCONTINUITY_MS = 100;

export interface VideoUrlProps {
  url: string;
  authHeaders?: { [header: string]: string };
  authQueryParams?: { [param: string]: string };
}

export interface VideoSegment extends Immutable<PlaylistItem> {
  /**
   * An array of 100 numbers representing a 10x10 grid overlaid on the video.
   * The value of each cell is in the range [0, 3], giving the amount of motion
   * detected in that cell.
   */
  readonly motionGrid: readonly number[];
  /** The maximum value in motionGrid. */
  readonly intensity: number;
  /** True if there is a gap between this segment and the previous segment. */
  readonly discontinuity: boolean;
  /** The segment uri. */
  readonly uri: string;
}

export interface ParsedPlaylist {
  readonly type: 'playlist';
  readonly m3u8: string;
  readonly segments: readonly VideoSegment[];
  readonly ttf: ScaleLinear<number, number>;
}

export interface ParsedSetLevelPlaylist {
  readonly type: 'set-level';
  readonly m3u8: string;
  readonly childM3u8: readonly string[];
}

/**
 * Parse an `m3u8` file from a local device or cloud.
 *
 * The returned object has a normalized `m3u8` file, an array of parsed segments,
 * and a "time translation function" for converting from wall-clock-time to
 * segment time and vice-versa.
 */
export function parseSolinkPlaylist(playlist: string): ParsedPlaylist | ParsedSetLevelPlaylist {
  const { m3u8, segments, childM3u8 } = _parsePlayList(playlist);
  const ttf = _makeTimeTranslationFunction(segments);

  if (childM3u8.length > 0) {
    return {
      type: 'set-level',
      m3u8,
      childM3u8,
    };
  }
  return {
    type: 'playlist',
    m3u8,
    segments,
    ttf,
  };
}

const _storePlayerDomain = (recordingData: VideoSegment[]) => {
  let playTime = 0;
  const playerDomain: number[] = [];

  for (let i = 0; i < recordingData.length; i++) {
    const { start, end } = recordingData[i];
    const duration = end - start;

    playerDomain.push(playTime);
    playerDomain.push(playTime + duration);
    playTime += duration;
  }

  return {
    playerDomain,
    playTime,
  };
};

const _storeRecordingDomain = (recordingData: VideoSegment[]) => {
  const recordingDomain: number[] = [];

  for (let i = 0; i < recordingData.length; i++) {
    const { start, end } = recordingData[i];
    recordingDomain.push(start);
    recordingDomain.push(end);
  }

  return recordingDomain;
};

/**
 * Returns a `ScaleLinear` which translates from player time to wall clock time.
 */
function _makeTimeTranslationFunction(segments: VideoSegment[]): ScaleLinear<number, number> {
  const { playerDomain } = _storePlayerDomain(segments);
  const recordingDomain = _storeRecordingDomain(segments);
  // Domain is player time, range is wall-clock time.
  const ttf = scaleLinear().domain(playerDomain).range(recordingDomain);
  return ttf;
}

/**
 * Decode encoded AI string to extract people and vehicle count.
 *
 * Protocol: in decoded form: `version | data` where data depends on version:
 *
 *  version 1: `version | person | vehicle` where person and vehicle are 0 or 1, depending on person or vehicle presence
 *  version 2: `version | person | vehicle` where person and vehicle are numbers in range [0-255],
 *             255 indicating the disabled type
 *  version 3: `version [ | type | length | values [ | ...]]`
 *
 * In version 3, the values depend on the type:
 *  type 2 is like version 2, values are `person | vehicle`  with values in range [0-255]
 *  type 3 has the same format, but indicates filtered counts with ROI (length 2 constant as well)
 *
 * The decoder is able to handle any of the 3 versions, and if available, prefers type 2 in version 3 over type 3
 * because AI data is only used in search and there is no reason to pre-filter the search results using motion ROI.

 * @param {string} encodedAI encoded AI string as extracted from m3u8 AI extension
 */
function decodeAI(encodedAI: string): PlaylistItem['ai'] | undefined {
  if (!encodedAI) {
    return undefined;
  }

  let buf: string;
  try {
    buf = atob(encodedAI);
  } catch (_error) {
    console.error('[decodeAI]  invalid encoding');
    return undefined;
  }

  if (buf.length < 3) {
    console.error('[decodeAI]  invalid length');
    return undefined;
  }

  interface TLV {
    type: number;
    len: number;
    val: number[];
  }

  const processCode = (person: number, vehicle: number) => {
    const decoded: PlaylistItem['ai'] = {};
    const code = { person, vehicle };
    const options: ('person' | 'vehicle')[] = ['person', 'vehicle'];
    for (const field of options) {
      if (code[field] >= 0) {
        if (code[field] < AI_VALUE_UPPER_BOUND) {
          decoded[field] = code[field];
        } else {
          decoded[field] = -1;
        }
      } else {
        decoded[field] = 0;
      }
    }

    return decoded;
  };

  const version = buf.charCodeAt(0);

  if (version === 2) {
    return processCode(buf.charCodeAt(1), buf.charCodeAt(2));
  } else if (version === 3) {
    const tlvsByType: { [key: number]: TLV } = {};
    let tlv: TLV = { type: -1, len: -1, val: [] };

    for (let index = 1; index < buf.length; index++) {
      if (tlv.type === -1) {
        tlv.type = buf.charCodeAt(index);
      } else if (tlv.len === -1) {
        tlv.len = buf.charCodeAt(index);
      } else if (tlv.val.length < tlv.len) {
        tlv.val.push(buf.charCodeAt(index));
      }

      if (tlv.val.length === tlv.len) {
        tlvsByType[tlv.type] = tlv;
        tlv = { type: -1, len: -1, val: [] };
      }
    }

    // prefer version 2 (unfiltered) over version 3 (filtered)
    if (tlvsByType[2]) {
      return processCode(tlvsByType[2].val[0], tlvsByType[2].val[1]);
    } else if (tlvsByType[3]) {
      return processCode(tlvsByType[3].val[0], tlvsByType[3].val[1]);
    }
    console.error(
      `[ROIIndexer.decodeAI]  Unable to recognize any supported AI data type in ` +
        `encoded AI string: ${encodedAI} decoded as: `,
      tlvsByType,
    );
  } else {
    console.error('[ROIIndexer.decodeAI]  Unsupported version: ' + version);
  }
}

/**
 * LazyVideoSegment is a VideoSegment which lazily computes the `ai`, `intensity`,
 * and `motionGrid` parameters on demand.  These parameters involve a lot of
 * number crunching, so defering parsing them until they are actually used can
 * make the app seem more responsive.
 */
class LazyVideoSegment implements VideoSegment {
  public readonly start: number;
  public readonly end: number;
  public discontinuity: boolean;
  public readonly uri: string;
  private readonly _aiStr: string | undefined;
  private readonly _motionGridStr: string | undefined;
  private _ai: VideoSegment['ai'];
  private _motionGrid: number[] | undefined;
  private _intensity: number | undefined;

  constructor(
    start: number,
    end: number,
    discontinuity: boolean,
    aiStr: string | undefined,
    motionGridStr: string | undefined,
    uri: string,
  ) {
    this.start = start;
    this.end = end;
    this.discontinuity = discontinuity;
    this._aiStr = aiStr;
    this._motionGridStr = motionGridStr;
    this.uri = uri;
  }

  get ai(): VideoSegment['ai'] {
    if (this._aiStr && !this._ai) {
      this._ai = decodeAI(this._aiStr);
    }
    return this._ai;
  }

  get motionGrid(): number[] {
    if (this._motionGrid === undefined) {
      this._motionGrid = decodeMotionGrid(this._motionGridStr) || [];
    }
    return this._motionGrid;
  }

  get intensity(): number {
    if (this._intensity === undefined) {
      const motionGrid = this.motionGrid;
      this._intensity = motionGrid.length > 0 ? Math.max(...motionGrid) : 0;
    }
    return this._intensity;
  }
}

/**
 * Parse segment data from a playlist in one pass.
 *
 * `#EXT-SOLINK-` tags apply to the previous segment instead of the next segment
 * (non-standard, but what the VMS produces).
 */
const _parsePlayList = (
  m3u8: string,
): { m3u8: string; segments: VideoSegment[]; childM3u8: string[] } => {
  const childM3u8: string[] = [];
  const segments: VideoSegment[] = [];
  let runningTimeMs = 0;

  let start: number | undefined;
  let end: number | undefined;
  let discontinuity = false;
  let aiStr: string | undefined;
  let motionGridStr: string | undefined;
  let duration: number | undefined;
  let uri = '';
  let parsingTrailer = false;

  const emitSegment = () => {
    if (start !== undefined && end !== undefined) {
      segments.push(new LazyVideoSegment(start, end, discontinuity, aiStr, motionGridStr, uri));
    } else if (duration !== undefined) {
      segments.push(
        new LazyVideoSegment(
          runningTimeMs,
          runningTimeMs + duration,
          discontinuity,
          aiStr,
          motionGridStr,
          uri,
        ),
      );
      runningTimeMs += duration;
    }
    start = undefined;
    end = undefined;
    discontinuity = false;
    aiStr = undefined;
    motionGridStr = undefined;
    duration = undefined;
    uri = '';
  };

  const len = m3u8.length;
  let offset = 0;
  while (offset < len) {
    let newline = m3u8.indexOf('\n', offset);
    if (newline === -1) {
      newline = len;
    }
    let line = m3u8.slice(offset, newline);
    offset = newline + 1;
    line = line.trim();

    if (parsingTrailer) {
      if (line.startsWith(SOLINK_MOTIONGRID_TAG)) {
        motionGridStr = line.slice(SOLINK_MOTIONGRID_TAG.length);
        continue;
      }
      if (line.startsWith(SOLINK_AI_TAG)) {
        aiStr = line.slice(SOLINK_AI_TAG.length);
        continue;
      }
      parsingTrailer = false;
      emitSegment();
    }

    if (line === '') {
      continue;
    }

    if (line.charCodeAt(0) !== 35) {
      if (
        line.includes('.m3u8') &&
        (line.includes('video.m3u8') ||
          line.includes('alarm.m3u8') ||
          line.includes('share-playlist.m3u8'))
      ) {
        childM3u8.push(line);
      }

      const media = parseMediaUrl(line, duration);
      if (media) {
        if (media.start !== undefined && media.end !== undefined) {
          start = media.start;
          end = media.end;
        }
        parsingTrailer = true;
      }
      continue;
    }

    if (line === '#EXT-X-DISCONTINUITY') {
      discontinuity = true;
    } else if (line.startsWith('#EXTINF')) {
      const colon = line.indexOf(':');
      if (colon !== -1) {
        const extinfDuration = parseFloat(line.slice(colon + 1));
        if (!isNaN(extinfDuration)) {
          duration = extinfDuration * 1000;
        }
      }

      let nextNewline = m3u8.indexOf('\n', offset);
      if (nextNewline === -1) {
        nextNewline = len;
      }
      uri = m3u8.slice(offset, nextNewline).trim();
    }
  }

  if (parsingTrailer) {
    emitSegment();
  }

  return { m3u8, segments, childM3u8 };
};

const playlistUrlRegex = /^(?:.*\/)?(.+?)_(.*)\.(ts|mp4)/;
const playlistFragUrlRegex = /^(?:.*\/)?frag_(.+?)_(.*)_(.*)_(.*)\.(ts|mp4)/;
const SOLINK_MOTIONGRID_TAG = '#EXT-SOLINK-MOTIONGRID:';
const SOLINK_AI_TAG = '#EXT-SOLINK-AI:';

/**
 * Parse start/end from a segment URI. Returns undefined if this is not a media URL.
 * Same matching as develop's `playlistUrlRegex` (not end-anchored; path can be
 * captured as the start group). `frag_` URLs keep their dedicated regex.
 */
function parseMediaUrl(
  line: string,
  extinfDurationMs: number | undefined,
): { start?: number; end?: number } | undefined {
  const lastSlash = line.lastIndexOf('/');
  let filename = lastSlash >= 0 ? line.slice(lastSlash + 1) : line;
  const query = filename.indexOf('?');
  if (query !== -1) {
    filename = filename.slice(0, query);
  }

  if (filename.startsWith('frag_')) {
    const fragMatch = playlistFragUrlRegex.exec(line);
    if (!fragMatch) {
      return playlistUrlRegex.exec(line) ? {} : undefined;
    }
    const fragStart = parseInt(fragMatch[2], 10);
    if (!isNaN(fragStart)) {
      return { start: fragStart, end: fragStart + (extinfDurationMs ?? 0) };
    }
    return {};
  }

  const match = playlistUrlRegex.exec(line);
  if (!match) {
    return;
  }

  const start = parseInt(match[1], 10);
  const mediaDuration = parseInt(match[2], 10);
  if (!isNaN(start) && !isNaN(mediaDuration)) {
    return { start, end: start + mediaDuration };
  }
  return {};
}
